import { useLayoutEffect, useRef } from 'react';

/**
 * Toolbar responsive con buscador absolute izquierda + botones centrados al toolbar.
 *
 * Si el buscador (que crece según los filtros activos) llegara a traslapar los botones
 * centrados, aplica `padding-left` dinámico al wrapper de botones para empujarlos a la
 * derecha la cantidad MÍNIMA necesaria (+16px de buffer). Cuando hay espacio, vuelven
 * al centro del toolbar.
 *
 * Uso:
 * ```jsx
 * const { searchRef, buttonsRef } = useToolbarResponsive([filter, selectedCount]);
 * return (
 *   <div className="bg-white border rounded p-3 relative">
 *     <div ref={searchRef} className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
 *       ...inputs/selects/datepickers...
 *     </div>
 *     <div ref={buttonsRef} className="flex items-center justify-center gap-2 flex-wrap">
 *       ...botones...
 *     </div>
 *   </div>
 * );
 * ```
 *
 * @param {Array} deps Dependencies que cuando cambien deben re-medir (ej. estado de filtros, selecciones).
 * @returns {{searchRef, buttonsRef}}
 */
export default function useToolbarResponsive(deps = []) {
  const searchRef = useRef(null);
  const buttonsRef = useRef(null);

  useLayoutEffect(() => {
    const search = searchRef.current;
    const buttons = buttonsRef.current;
    if (!search || !buttons) return;

    const adjust = () => {
      // Reset para medir naturalmente la posición del primer botón centrado
      buttons.style.paddingLeft = '0px';
      const firstBtn = buttons.firstElementChild;
      if (!firstBtn) return;

      const searchRight = search.getBoundingClientRect().right;
      const firstBtnLeft = firstBtn.getBoundingClientRect().left;
      const buffer = 16; // separación mínima entre buscador y botones
      const overlap = searchRight + buffer - firstBtnLeft;

      if (overlap > 0) {
        // OJO: con justify-center, padding-left=X mueve el contenido solo X/2 a la derecha
        // (el centrado divide el espacio sobrante). Por eso aplicamos 2*overlap para que
        // los botones se desplacen exactamente `overlap` píxeles y dejen el buffer correcto.
        buttons.style.paddingLeft = `${overlap * 2}px`;
      }
      // overlap <= 0 → paddingLeft queda en 0 → botones centrados al toolbar
    };

    adjust();
    const ro = new ResizeObserver(adjust);
    ro.observe(search);
    if (buttons.parentElement) ro.observe(buttons.parentElement);
    // Observar hijos: aparición/desaparición de elementos (contadores, badges) cambia ancho
    Array.from(buttons.children).forEach(child => ro.observe(child));
    window.addEventListener('resize', adjust);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', adjust);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { searchRef, buttonsRef };
}
